import React, { useEffect, useState, useMemo } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import './ComplianceDetailPage.css';

interface ComplianceItem {
  id: string;
  framework: string;
  compliance: number;
  controls: number;
  passingControls: number;
  lastAudit: string;
  status: string;
  severity: string;
}

const ComplianceDetailPage: React.FC = () => {
  const { t } = useLanguage();
  const [selectedItem, setSelectedItem] = useState<ComplianceItem | null>(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [severityFilter, setSeverityFilter] = useState('all');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const complianceData: ComplianceItem[] = useMemo(() => ([
    {
      id: 'c1',
      framework: 'PCI-DSS',
      compliance: 95,
      controls: 12,
      passingControls: 12,
      lastAudit: '2024-01-15',
      status: 'Compliant',
      severity: 'Critical'
    },
    {
      id: 'c2',
      framework: 'HIPAA',
      compliance: 88,
      controls: 18,
      passingControls: 16,
      lastAudit: '2024-01-14',
      status: 'Review',
      severity: 'High'
    },
    {
      id: 'c3',
      framework: 'GDPR',
      compliance: 92,
      controls: 15,
      passingControls: 14,
      lastAudit: '2024-01-16',
      status: 'Compliant',
      severity: 'Critical'
    },
    {
      id: 'c4',
      framework: 'ISO27001',
      compliance: 85,
      controls: 20,
      passingControls: 17,
      lastAudit: '2024-01-13',
      status: 'Review',
      severity: 'High'
    },
    {
      id: 'c5',
      framework: 'NIST',
      compliance: 90,
      controls: 22,
      passingControls: 20,
      lastAudit: '2024-01-15',
      status: 'Compliant',
      severity: 'Medium'
    },
    {
      id: 'c6',
      framework: 'SOC2',
      compliance: 78,
      controls: 16,
      passingControls: 12,
      lastAudit: '2024-01-12',
      status: 'NonCompliant',
      severity: 'Critical'
    },
  ]), []);

  const filteredItems = useMemo(() => {
    return complianceData.filter(item => {
      const statusMatch = statusFilter === 'all' || item.status === statusFilter;
      const severityMatch = severityFilter === 'all' || item.severity === severityFilter;
      return statusMatch && severityMatch;
    });
  }, [statusFilter, severityFilter, complianceData]);

  const getSeverityColor = (severity: string) => {
    switch(severity) {
      case 'Critical': return '#ff4444';
      case 'High': return '#ff8c00';
      case 'Medium': return '#ffd700';
      case 'Low': return '#22c55e';
      default: return '#666666';
    }
  };

  const getStatusColor = (status: string) => {
    switch(status) {
      case 'Compliant': return '#22c55e';
      case 'Review': return '#ffd700';
      case 'NonCompliant': return '#ff4444';
      default: return '#666666';
    }
  };

  if (!selectedItem && filteredItems.length > 0) {
    setSelectedItem(filteredItems[0]);
  }


  return (
    <div className="compliance-detail-container">
      <div className="detail-header">
        <h1>{t('compliance.detailTitle')}</h1>
        <p>{t('compliance.detailDescription')}</p>
      </div>

      <div className="filter-section">
        <div className="filter-group">
          <label>{t('compliance.filters.status')}</label>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">{t('compliance.filters.all')}</option>
            <option value="Compliant">Compliant</option>
            <option value="Review">Review</option>
            <option value="NonCompliant">Non-Compliant</option>
          </select>
        </div>

        <div className="filter-group">
          <label>{t('compliance.filters.framework')}</label>
          <select value={severityFilter} onChange={(e) => setSeverityFilter(e.target.value)}>
            <option value="all">{t('compliance.filters.all')}</option>
            <option value="Critical">Critical</option>
            <option value="High">High</option>
            <option value="Medium">Medium</option>
            <option value="Low">Low</option>
          </select>
        </div>

        <div className="filter-stat">
          {t('compliance.filters.searchResults')}: {filteredItems.length}
        </div>
      </div>

      <div className="content-layout">
        <div className="table-section">
          <div className="table-header">
            <div>{t('compliance.columns.framework')}</div>
            <div>{t('compliance.columns.compliance')}</div>
            <div>{t('compliance.columns.controls')}</div>
            <div>{t('compliance.columns.status')}</div>
            <div>{t('compliance.columns.severity')}</div>
            <div>{t('compliance.columns.lastAudit')}</div>
          </div>

          <div className="table-body">
            {filteredItems.length > 0 ? (
              filteredItems.map(item => (
                <div
                  key={item.id}
                  className={`table-row ${selectedItem?.id === item.id ? 'active' : ''}`}
                  onClick={() => setSelectedItem(item)}
                >
                  <div className="table-row-cell">{item.framework}</div>
                  <div className="table-row-cell">{item.compliance}%</div>
                  <div className="table-row-cell">{item.passingControls}/{item.controls}</div>
                  <div className="table-row-cell">
                    <span className="status-badge" style={{backgroundColor: getStatusColor(item.status) + '20', color: getStatusColor(item.status)}}>
                      {item.status}
                    </span>
                  </div>
                  <div className="table-row-cell">
                    <span className="severity-badge" style={{backgroundColor: getSeverityColor(item.severity) + '20', color: getSeverityColor(item.severity)}}>
                      {item.severity}
                    </span>
                  </div>
                  <div className="table-row-cell">{item.lastAudit}</div>
                </div>
              ))
            ) : (
              <div className="empty-state">
                <p>No compliance frameworks found</p>
              </div>
            )}
          </div>
        </div>

        <div className="detail-section">
          {selectedItem ? (
            <div className="detail-card">
              <h2>{selectedItem.framework}</h2>
              <div className="detail-row">
                <label>ID</label>
                <div className="detail-row-value">{selectedItem.id}</div>
              </div>
              <div className="detail-row">
                <label>Framework</label>
                <div className="detail-row-value">{selectedItem.framework}</div>
              </div>
              <div className="detail-row">
                <label>Compliance</label>
                <div className="detail-row-value">{selectedItem.compliance}%</div>
              </div>
              <div className="detail-row">
                <label>Controls</label>
                <div className="detail-row-value">{selectedItem.passingControls} / {selectedItem.controls} Passing</div>
              </div>
              <div className="detail-row">
                <label>Status</label>
                <div className="detail-row-value">
                  <span className="detail-badge" style={{backgroundColor: getStatusColor(selectedItem.status) + '20', color: getStatusColor(selectedItem.status)}}>
                    {selectedItem.status}
                  </span>
                </div>
              </div>
              <div className="detail-row">
                <label>Severity</label>
                <div className="detail-row-value">
                  <span className="detail-badge" style={{backgroundColor: getSeverityColor(selectedItem.severity) + '20', color: getSeverityColor(selectedItem.severity)}}>
                    {selectedItem.severity}
                  </span>
                </div>
              </div>
              <div className="detail-row">
                <label>Last Audit</label>
                <div className="detail-row-value">{selectedItem.lastAudit}</div>
              </div>
              <div className="detail-row">
                <label>Description</label>
                <div className="detail-row-value">
                  {selectedItem.framework} compliance framework with {selectedItem.controls} security controls required for organizational security and regulatory adherence.
                </div>
              </div>
            </div>
          ) : (
            <div className="detail-card empty-detail">
              <div className="empty-state-message">
                <p>📋 목록에서 규정을 선택하세요.</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ComplianceDetailPage;
